import _ from 'lodash';
import moment from 'moment';

const ObjectUtils = (() => {
  const service = {};

  service.filterObject = (object, filter) => {
    const newObject = {};
    Object.keys(object)
      .filter(filter)
      .forEach((key) => {
        newObject[key] = object[key];
      });
    return {
      value: newObject,
      raw: object,
    };
  };

  service.toLargestKeySet = (list) => {
    if (!list || list.length === 0) {
      return [];
    }
    const shallowCopy = list.slice();
    const keySet = Object.keys(
      shallowCopy.sort((one, two) => {
        const oneSize = Object.keys(one).length;
        const twoSize = Object.keys(two).length;
        if (oneSize < twoSize) {
          return 1;
        }
        if (oneSize > twoSize) {
          return -1;
        }
        return 0;
      })[0]
    );
    return keySet;
  };

  service.formatStringAsJSONArray = (value) => {
    if (Array.isArray(value)) {
      return value;
    }

    if (value && value.length > 0) {
      try {
        return JSON.parse(value);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  service.formatStringAsJSONObject = (value) => {
    if (!value) {
      return {};
    }
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (e) {
        return {};
      }
    } else if (typeof value === 'object') {
      return value;
    }
    return {};
  };

  const clearNullValues = (passedObject) => {
    const iterableObject = passedObject;

    Object.keys(iterableObject).forEach((key) => {
      if (iterableObject[key] === null) {
        iterableObject[key] = '';
      }

      if (typeof iterableObject[key] === 'object') {
        clearNullValues(iterableObject[key]);
      }
    });

    return iterableObject;
  };

  const dynamicSort = (propertyParam) => {
    let property = propertyParam;
    let sortOrder = 1;

    if (property[0] === '-') {
      sortOrder = -1;
      property = property.substr(1);
    }

    return (a, b) => {
      if (sortOrder === -1) {
        return b[property].toString().localeCompare(a[property]);
      }
      return a[property].toString().localeCompare(b[property]);
    };
  };

  const parseAccounts = (accounts) => {
    let idIndex;
    let externalAccountIdIndex;
    const rows = [];
    const filteredRows = [];
    const fixedAccounts = [];
    let parsedResponse = {};

    const headers = Object.keys(accounts[0]);
    const filteredHeaders = headers.filter((header, index) => {
      if (header === 'id') {
        idIndex = index;
      }
      if (header === 'externalAccountId') {
        externalAccountIdIndex = index;
      }
      return header === 'id' || header === 'externalAccountId';
    });
    const casedHeaders = filteredHeaders.map(header => _.startCase(header));

    accounts.forEach((account) => {
      const row = Object.values(account);
      const fixedAccount = account;
      rows.push(row);
      const filteredRow = row.filter(
        (_row, index) => index === idIndex || index === externalAccountIdIndex
      );

      if (account.customerIds !== null && account.customerIds !== undefined) {
        let { customerIds, } = account;

        customerIds = customerIds.replace(/["/[/]/g, '');
        customerIds = _.trimEnd(customerIds, ']');
        customerIds = _.split(customerIds, ',');

        fixedAccount.customerIds = customerIds;
      }

      if (account.ebillingIds !== null && account.ebillingIds !== undefined) {
        let { ebillingIds, } = account;

        ebillingIds = ebillingIds.replace(/["/[/]/g, '');
        ebillingIds = _.trimEnd(ebillingIds, ']');
        ebillingIds = _.split(ebillingIds, ',');

        fixedAccount.ebillingIds = ebillingIds;
      }

      if (account.serviceAddresses !== null && account.serviceAddresses !== undefined) {
        let { serviceAddresses, } = account;

        serviceAddresses = serviceAddresses.replace(/["/[/]/g, '');
        serviceAddresses = _.trimEnd(serviceAddresses, ']');
        serviceAddresses = _.split(serviceAddresses, ',');

        fixedAccount.serviceAddresses = serviceAddresses;
      }

      fixedAccounts.push(fixedAccount);
      filteredRows.push(filteredRow);
    });

    casedHeaders.unshift('');

    parsedResponse = {
      fullData: {
        headers,
        rows,
      },

      tableData: {
        headers: casedHeaders,
        rows: filteredRows,
      },

      fixedAccounts,
    };

    return parsedResponse;
  };

  const parseBills = (bills) => {
    let idIndex;
    let paidIndex;
    let isPaidIndex;
    let billAmountIndex;
    let billBalanceIndex;
    let dueDateIndex;
    const rows = [];
    const filteredRows = [];
    let parsedResponse = {};

    const headers = Object.keys(bills[0]).map(header => _.startCase(header));
    const filteredHeaders = headers.filter((header, index) => {
      if (header === 'Id') {
        idIndex = index;
      }
      if (header === 'Paid') {
        paidIndex = index;
      }
      if (header === 'Is Paid') {
        isPaidIndex = index;
      }
      if (header === 'Bill Amount Value') {
        billAmountIndex = index;
      }
      if (header === 'Bill Balance Value') {
        billBalanceIndex = index;
      }
      if (header === 'Due Date') {
        dueDateIndex = index;
      }
      return (
        header === 'Id'
        || header === 'Paid'
        || header === 'Is Paid'
        || header === 'Bill Amount Value'
        || header === 'Bill Balance Value'
        || header === 'Due Date'
      );
    });
    const casedHeaders = filteredHeaders.map(header => _.startCase(header));

    const parsedBills = bills.map((bill) => {
      const row = Object.values(bill);

      if (row[paidIndex] !== undefined) {
        row[paidIndex] = _.upperCase(row[paidIndex].toString());
      }

      if (row[isPaidIndex] !== undefined) {
        row[isPaidIndex] = _.upperCase(row[isPaidIndex].toString());
      }

      row[dueDateIndex] = moment(row[dueDateIndex])
        .utc()
        .format();

      rows.push(row);

      const filteredRow = row.filter(
        (_row, index) => index === idIndex
          || index === paidIndex
          || index === isPaidIndex
          || index === billAmountIndex
          || index === billBalanceIndex
          || index === dueDateIndex
      );

      filteredRows.push(filteredRow);

      const parsedBill = {};

      headers.forEach((header, index) => {
        parsedBill[header] = row[index];
      });

      return parsedBill;
    });

    casedHeaders.unshift('');

    parsedResponse = {
      fullData: {
        headers,
        rows,
      },

      tableData: {
        headers: casedHeaders,
        rows: filteredRows,
      },

      parsedBills,
    };

    return parsedResponse;
  };

  service.clearNullValues = clearNullValues;
  service.dynamicSort = dynamicSort;
  service.parseAccounts = parseAccounts;
  service.parseBills = parseBills;

  return service;
})();

export default ObjectUtils;
